import React from "react"
import { useSelector } from "react-redux"

import {
    LIST, VIEW, EDIT, YES, VALUE, UNLIMITED, ACTIVE,
} from "const"
import Images from "assets/images"
import { hasAccess, hasProjectAccess } from "client/utils"
import {
    PRM_ACCOUNT,
    PRM_BILLING,
    PRM_CC_LIBRARY,
    PRM_CC_COMPLIANCE,
    PRM_CC_REVIEW,
    PRM_CC_REQUIREMENT,
    PRM_CC_REQUIREMENT_REVIEW,
    PRM_DRIVE,
    PRM_GROUP,
    PRM_LIFECYCLE,
    PRM_PERMISSIONS,
    PRM_PROJECT,
    PRM_PROJECT_SETTINGS,
    PRM_QID_COMPANY,
    PRM_QID_PRODUCT,
    PRM_SIL_DEVICE,
    PRM_SIL_SUBSYSTEM,
    PRM_SIL_SAFETY_FUNCTION,
    PRM_SIL_RESULTS,
    PRM_TEAM,
    PRM_TASK,
    PRM_OKR_SESSION,
    PRM_RRS_CATEGORY,
    PRM_RRS_TEMPLATE,
    PRM_STUDY,
    PRM_STUDY_SESSION,
    PRM_MARKOV_MODEL,

    // pdf report
    PRM_CC_RESULTS_PDF,
    PRM_SIL_RESULTS_PDF,

    StoreType,
    ProjectActiveModules,
    moduleIds,
} from "client/store"

import { Logo } from "client/components"
import {
    ProjectMenuLogoWrapper,
    ProjectMenuLogoTitleColumn,
    ProjectMenuLogoTitle,
} from "client/parts/projectMenu/styled"


export const loginRoute = "/login"
export const registerRoute = "/register"
export const confirmEmailRoute = "/confirmEmail"
export const forgotPasswordRoute = "/forgotPassword"
export const resetPasswordRoute = "/resetPassword"

export const completePaymentRoute = "/completePayment"
export const profileRoute = "/profile"
export const profileEditRoute = `/profile/${EDIT}`

export const mtRoute = "/mt"
export const mtAccountRoute = "/mt/account"
export const mtAccountsRoute = "/mt/accounts"
export const mtBillingRoute = "/mt/billing"
export const mtPackagesRoute = "/mt/packages"
export const mtPermissionsRoute = "/mt/permissions"
export const mtGroupsRoute = "/mt/groups"
export const mtUsersRoute = "/mt/users"
export const mtTagsRoute = "/mt/tags"
export const mtModulesRoute = "/mt/modules"
export const mtSettingsRoute = "/mt/settings"
export const mtTranslationsRoute = "/mt/translations"
export const mtSqlRoute = "/mt/sql"

export const projectsRoute = "/projects"
export const projectDetailsRoute = "/details"
export const projectGroupsRoute = "/groups"
export const projectUsersRoute = "/users"
export const projectPermissionsRoute = "/permissions"
export const projectSettingsRoute = "/settings"
export const projectBaseRoute = `${projectsRoute}/${VIEW}/:projectId`

export const tasksRoute = "/tasks"
export const driveRoute = "/drive"
export const cloudDriveAuthResponseRoute = "/cloudDriveAuthResponse"

export const ccRoute = "/cc"
export const ccComplianceSetsRoute = "/cc/complianceSets"
export const ccCompliantItemsRoute = "/compliantItems"
export const ccFixComplianceAnswer = "/cc/fixComplianceAnswer"
export const projectCcComplianceRoute = "/cc/compliance"
export const projectCcReviewRoute = "/cc/review"
export const projectCcRequirementsRoute = "/cc/requirements"
export const projectCcRequirementsReviewRoute = "/cc/requirementsReview"

export const silRoute = "/sil"
export const silDevicesRoute = "/sil/devices"
export const silSubsystemsRoute = "/sil/subsystems"
export const silSafetyFunctionsRoute = "/sil/safetyFunctions"
export const silResultsRoute = "/sil/results"
export const silCalculationAssumptionsRoute = "/sil/calculationAssumptions"

export const qidRoute = "/qid"
export const qidCompaniesRoute = "/qid/companies"
export const qidProductsRoute = "/qid/products"

export const lifecycleRoute = "/lifecycle"
export const lifecyclesRoute = "/lifecycle/lifecycles"

export const markovRoute = "/markov"
export const markovModelsRoute = "/markov/models"

export const reportingRoute = "/reporting"

export const okrRoute = "/okr"
export const okrSessionsRoute = "/okr/sessions"
export const okrObjectivesAllRoute = "/objectives/all"
export const okrObjectivesNestedRoute = "/objectives/nested"
export const okrObjectivesTreeRoute = "/objectives/tree"
export const okrKeyResultsRoute = "/keyResults"
export const okrSessionBaseRoute = `${okrSessionsRoute}/${VIEW}/:sessionId`

export const rrsRoute = "/rrs"
export const rrsCategoriesRoute = "/rrs/categories"
export const rrsTemplatesRoute = "/rrs/templates"

export const studyRoute = "/study"
export const studiesRoute = "/study/studies"
export const studySessionsRoute = "/study/sessions"

export interface RouteItem {
    path: string
    title: any
    access: boolean
    routes?: RouteItem[]
}
export interface ProjectRouteItem extends RouteItem {
    fitContent?: boolean
}

export function CreateRoutes() {
    const lang = useSelector((state: StoreType) => state.system.lang)
    const packageActive = useSelector((state: StoreType) => state.system.packageActive)
    const packageSettings = useSelector((state: StoreType) => state.system.packageSettings)
    const userSettings = useSelector((state: StoreType) => state.system.userSettings)
    const moduleList = useSelector((state: StoreType) => state.module.moduleList)
    const project = useSelector((state: StoreType) => state.project.projectView)
    const accountId = useSelector((state: StoreType) => state.system.userData.accountId)

    const {
        projectModuleAccess,
        taskModuleAccess,
        driveSpace,
        driveSpaceValue,
        rrsRiskSpaceModuleAccess,
        studyModuleAccess,
        ccModuleAccess,
        ccRequirementsModuleAccess,
        silModuleAccess,
        qidModuleAccess,
        lifecycleModuleAccess,
        markovModuleAccess,
        okrModuleAccess,
    } = packageSettings

    const {
        study,
        riskSpace,
        cc,
        sil,
        qid,
        lifecycle,
        markov,
    } = moduleIds

    const projectActiveModules: ProjectActiveModules = project && project.modules ? JSON.parse(project.modules) : {}

    let driveAccess = false
    if (accountId !== 1 && driveSpace === VALUE && driveSpaceValue > 0) driveAccess = true
    if (accountId !== 1 && driveSpace === UNLIMITED) driveAccess = true

    const ccPdfAccess = hasProjectAccess(PRM_CC_RESULTS_PDF, VIEW)
    const silPdfAccess = hasProjectAccess(PRM_SIL_RESULTS_PDF, VIEW)
    const reportAccess = ccPdfAccess || silPdfAccess

    const packageExpireDate = packageActive ? new Date(packageActive.dateEnd) : null
    const now = new Date()
    const expired = packageExpireDate ? packageExpireDate <= now : false

    const billingRoute: RouteItem = {
        path: mtBillingRoute,
        title: lang.billing,
        access: (accountId !== 1 && hasAccess(PRM_BILLING, LIST)),
    }

    const routesExpired: RouteItem[] = [{
        path: mtRoute,
        title: lang.accountManagement,
        access: true,
        routes: [billingRoute],
    }]

    const routes: RouteItem[] = [
        {
            path: mtRoute,
            title: lang.accountManagement,
            access: true,
            routes: [
                /* {
                    path: mtRoute,
                    title: lang.home,
                    access: true,
                }, */
                {
                    path: mtAccountRoute,
                    title: lang.account,
                    access: (accountId !== 1 && hasAccess(PRM_ACCOUNT, VIEW)),
                },
                {
                    path: mtAccountsRoute,
                    title: lang.accounts,
                    access: (accountId === 1),
                },

                billingRoute,

                {
                    path: mtPackagesRoute,
                    title: lang.packages,
                    access: (accountId === 1),
                },
                {
                    path: mtPermissionsRoute,
                    title: lang.permissions,
                    access: hasAccess(PRM_PERMISSIONS, LIST),
                },
                {
                    path: mtGroupsRoute,
                    title: lang.groups,
                    access: hasAccess(PRM_GROUP, LIST),
                },
                {
                    path: mtUsersRoute,
                    title: lang.users,
                    access: (accountId === 1),
                },
                {
                    path: mtModulesRoute,
                    title: lang.modules,
                    access: (accountId === 1),
                },
                {
                    path: mtTagsRoute,
                    title: lang.tags,
                    access: (accountId === 1),
                },
                {
                    path: mtSettingsRoute,
                    title: lang.settings,
                    access: true,
                },
                {
                    path: mtTranslationsRoute,
                    title: lang.translations,
                    access: (accountId === 1),
                },
                {
                    path: mtSqlRoute,
                    title: lang.sqlExecutor,
                    access: (accountId === 1),
                },
            ],
        },
        {
            path: tasksRoute,
            title: lang.tasks,
            access: (accountId !== 1 && taskModuleAccess === YES && hasAccess(PRM_TASK, LIST)),
        },
        {
            path: projectsRoute,
            title: lang.projects,
            access: (accountId !== 1 && projectModuleAccess === YES && hasAccess(PRM_PROJECT, LIST)),
        },
        {
            path: driveRoute,
            title: lang.drive,
            access: (accountId === 1 || (driveAccess && hasAccess(PRM_DRIVE, LIST))),
        },
        {
            path: rrsRoute,
            title: (accountId === 1 ? lang.rrsEngine : moduleList[riskSpace].name),
            access: (accountId === 1 || (rrsRiskSpaceModuleAccess === YES && moduleList[riskSpace].status === ACTIVE)),
            routes: [
                /* {
                    path: rrsRoute,
                    title: lang.home,
                    access: true,
                }, */
                {
                    path: rrsCategoriesRoute,
                    title: lang.lexicon,
                    access: hasAccess(PRM_RRS_CATEGORY, LIST),
                },
                {
                    path: rrsTemplatesRoute,
                    title: lang.riskSpaceTemplates,
                    access: hasAccess(PRM_RRS_TEMPLATE, LIST),
                },
            ],
        },
        {
            path: silRoute,
            title: moduleList[sil].name,
            access: (accountId === 1 || (silModuleAccess === YES && moduleList[sil].status === ACTIVE)),
            routes: [
                /* {
                    path: silRoute,
                    title: lang.home,
                    access: true,
                }, */
                {
                    path: silDevicesRoute,
                    title: lang.devices,
                    access: hasAccess(PRM_SIL_DEVICE, LIST),
                },
                {
                    path: silSubsystemsRoute,
                    title: lang.subsystems,
                    access: hasAccess(PRM_SIL_SUBSYSTEM, LIST),
                },
            ],
        },
        {
            path: qidRoute,
            title: moduleList[qid].name,
            access: (accountId === 1 || (qidModuleAccess === YES && moduleList[qid].status === ACTIVE)),
            routes: [
                /* {
                    path: qidRoute,
                    title: lang.home,
                    access: true,
                }, */
                {
                    path: qidCompaniesRoute,
                    title: lang.companies,
                    access: hasAccess(PRM_QID_COMPANY, LIST),
                },
                {
                    path: qidProductsRoute,
                    title: lang.products,
                    access: hasAccess(PRM_QID_PRODUCT, LIST),
                },
            ],
        },
        {
            path: ccRoute,
            title: moduleList[cc].name,
            access: (accountId === 1 || (ccModuleAccess === YES && moduleList[cc].status === ACTIVE)),
            routes: [
                /* {
                    path: ccRoute,
                    title: lang.home,
                    access: true,
                }, */
                {
                    path: ccComplianceSetsRoute,
                    title: lang.library,
                    access: hasAccess(PRM_CC_LIBRARY, LIST),
                },
                {
                    path: ccFixComplianceAnswer,
                    title: lang.ccFixComplianceAnswer,
                    access: accountId === 1,
                },
            ],
        },
        {
            path: lifecycleRoute,
            title: moduleList[lifecycle].name,
            access: (accountId === 1 || (lifecycleModuleAccess === YES && moduleList[lifecycle].status === ACTIVE)),
            routes: [
                /* {
                    path: lifecycleRoute,
                    title: lang.home,
                    access: true,
                }, */
                {
                    path: lifecyclesRoute,
                    title: lang.lifecycles,
                    access: hasAccess(PRM_LIFECYCLE, LIST),
                },
            ],
        },
        {
            path: markovRoute,
            title: moduleList[markov].name,
            access: (accountId === 1 || (markovModuleAccess === YES && moduleList[markov].status === ACTIVE)),
            routes: [
                {
                    path: markovRoute,
                    title: lang.home,
                    access: true,
                },
                {
                    path: markovModelsRoute,
                    title: lang.models,
                    access: hasAccess(PRM_MARKOV_MODEL, LIST),
                },
            ],
        },
        {
            path: okrRoute,
            title: lang.objectivesKeyResults,
            access: (accountId === 1 || okrModuleAccess === YES),
            routes: [
                {
                    path: okrRoute,
                    title: lang.home,
                    access: true,
                },
                {
                    path: okrSessionsRoute,
                    title: lang.sessions,
                    access: hasAccess(PRM_OKR_SESSION, LIST),
                },
            ],
        },
    ]

    const projectRoutes: ProjectRouteItem[] = [
        {
            path: "",
            title: (
                <ProjectMenuLogoWrapper title={project && `# ${project.id} - ${project.publicId} - ${project.name}`}>
                    <Logo
                        logo={(project && project.logo) || userSettings.project.defaultLogo || Images.noImage}
                        size={36}
                        borderRadius={18}
                    />
                    <ProjectMenuLogoTitleColumn>
                        <ProjectMenuLogoTitle>{project && `# ${project.id}`}</ProjectMenuLogoTitle>
                        <ProjectMenuLogoTitle>{project && project.name}</ProjectMenuLogoTitle>
                    </ProjectMenuLogoTitleColumn>
                </ProjectMenuLogoWrapper>
            ),
            fitContent: true,
            access: true,
            routes: [
                {
                    path: projectDetailsRoute,
                    title: lang.details,
                    access: true,
                },
                {
                    path: projectGroupsRoute,
                    title: lang.groups,
                    access: hasProjectAccess(PRM_TEAM, VIEW),
                },
                {
                    path: projectUsersRoute,
                    title: lang.users,
                    access: hasProjectAccess(PRM_TEAM, VIEW),
                },
                {
                    path: projectPermissionsRoute,
                    title: lang.permissions,
                    access: hasProjectAccess(PRM_PERMISSIONS, LIST),
                },
                {
                    path: projectSettingsRoute,
                    title: lang.settings,
                    access: hasProjectAccess(PRM_PROJECT_SETTINGS, VIEW),
                },
            ],
        },
        {
            path: tasksRoute,
            title: lang.tasks,
            access: taskModuleAccess === YES && hasProjectAccess(PRM_TASK, LIST),
        },
        {
            path: driveRoute,
            title: lang.drive,
            access: driveAccess && hasProjectAccess(PRM_DRIVE, LIST),
        },
        {
            path: rrsRoute,
            title: moduleList[riskSpace].name,
            access: rrsRiskSpaceModuleAccess === YES
                && moduleList[riskSpace].status === ACTIVE
                && projectActiveModules[riskSpace],
            routes: [
                /* { TODO: temporarily disabled
                    path: rrsCategoriesRoute,
                    title: lang.lexicon,
                    access: hasProjectAccess(PRM_RRS_CATEGORY, LIST),
                }, */
                {
                    path: rrsTemplatesRoute,
                    title: lang.riskSpaceTemplates,
                    access: hasProjectAccess(PRM_RRS_TEMPLATE, LIST),
                },
            ],
        },
        {
            path: studyRoute,
            title: moduleList[study].name,
            access: studyModuleAccess === YES
                && moduleList[study].status === ACTIVE
                && projectActiveModules[study],
            routes: [
                {
                    path: studiesRoute,
                    title: lang.studies,
                    access: hasProjectAccess(PRM_STUDY, LIST),
                },
                {
                    path: studySessionsRoute,
                    title: lang.sessions,
                    access: hasProjectAccess(PRM_STUDY_SESSION, LIST),
                },
            ],
        },
        {
            path: silRoute,
            title: moduleList[sil].name,
            access: silModuleAccess === YES
                && moduleList[sil].status === ACTIVE
                && projectActiveModules[sil],
            routes: [
                {
                    path: silDevicesRoute,
                    title: lang.devices,
                    access: hasProjectAccess(PRM_SIL_DEVICE, LIST),
                },
                {
                    path: silSubsystemsRoute,
                    title: lang.subsystems,
                    access: hasProjectAccess(PRM_SIL_SUBSYSTEM, LIST),
                },
                {
                    path: silSafetyFunctionsRoute,
                    title: lang.safetyFunctions,
                    access: hasProjectAccess(PRM_SIL_SAFETY_FUNCTION, LIST),
                },
                {
                    path: silResultsRoute,
                    title: lang.results,
                    access: hasProjectAccess(PRM_SIL_RESULTS, VIEW),
                },
                {
                    path: silCalculationAssumptionsRoute,
                    title: lang.calculationAssumptions,
                    access: true,
                },
            ],
        },
        {
            path: qidRoute,
            title: moduleList[qid].name,
            access: qidModuleAccess === YES
                && moduleList[qid].status === ACTIVE
                && projectActiveModules[qid],
            routes: [
                {
                    path: qidRoute,
                    title: lang.dashboard,
                    access: true,
                },
            ],
        },
        {
            path: ccRoute,
            title: moduleList[cc].name,
            access: ccModuleAccess === YES
                && moduleList[cc].status === ACTIVE
                && projectActiveModules[cc],
            routes: [
                {
                    path: projectCcComplianceRoute,
                    title: lang.compliance,
                    access: hasProjectAccess(PRM_CC_COMPLIANCE, VIEW),
                },
                {
                    path: projectCcReviewRoute,
                    title: lang.review,
                    access: hasProjectAccess(PRM_CC_REVIEW, VIEW),
                },
                {
                    path: projectCcRequirementsRoute,
                    title: lang.requirements,
                    access: ccRequirementsModuleAccess === YES && hasProjectAccess(PRM_CC_REQUIREMENT, LIST),
                },
                {
                    path: projectCcRequirementsReviewRoute,
                    title: lang.requirementsReview,
                    access: ccRequirementsModuleAccess === YES && hasProjectAccess(PRM_CC_REQUIREMENT_REVIEW, VIEW),
                },
                {
                    path: ccComplianceSetsRoute,
                    title: lang.library,
                    access: hasProjectAccess(PRM_CC_LIBRARY, LIST),
                },
            ],
        },
        {
            path: lifecycleRoute,
            title: moduleList[lifecycle].name,
            access: lifecycleModuleAccess === YES
                && moduleList[lifecycle].status === ACTIVE
                && projectActiveModules[lifecycle],
            routes: [
                {
                    path: lifecyclesRoute,
                    title: lang.lifecycles,
                    access: hasProjectAccess(PRM_LIFECYCLE, LIST),
                },
            ],
        },
        {
            path: markovRoute,
            title: moduleList[markov].name,
            access: markovModuleAccess === YES
                && moduleList[markov].status === ACTIVE
                && projectActiveModules[markov],
            routes: [
                {
                    path: markovRoute,
                    title: lang.home,
                    access: true,
                },
            ],
        },
        {
            path: reportingRoute,
            title: lang.reporting,
            access: reportAccess,
        },
    ]

    return {
        routes: expired ? routesExpired : routes,
        projectRoutes,
    }
}
